// src/pages/ChatSimulatorPage.js
// Page for the Chat Simulator Video Generator Tool

import React, { useState, useEffect, useRef, useCallback } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import OpenAI from "openai";

// Import the correct functions instance for the secondary project
import { storage, auth, functionsSecondary } from '../firebaseConfig'; // Ensure functionsSecondary is exported
import { ref, getDownloadURL, uploadBytes } from "firebase/storage";
import { httpsCallable } from "firebase/functions";

import { vibePresets } from "../data/vibePresets";
import { defaultScript as initialDefaultScript } from "../data/defaultScript";
import { defaultColors, voices, soundEffects as availableSoundEffects } from "../data/constants";
import { sanitize, sleep, audioDuration, playAudioFromBlob } from "../data/helpers";

// Initialize defaultScript with vibe objects
const defaultScript = initialDefaultScript.map(msg => {
  if (msg.type === "text") {
    const baseVibe = vibePresets["No Vibe"] || {};
    const existingVibe = msg.vibe || {};
    return { ...msg, vibe: { ...baseVibe, ...existingVibe, open: existingVibe.open || false, preset: existingVibe.preset || "No Vibe" } };
  }
  return msg;
});


// --- Input components ---
function ColorInput({ label, value, onChange, id }) {
  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="block text-gray-300 text-sm font-bold mb-1">
        {label}
      </label>
      <input id={id} type="color" value={value} onChange={onChange} className="rounded-md border border-slate-600 p-1 h-10 bg-slate-700 w-full" />
    </div>
  );
}

function TextInput({ label, value, onChange, id, type = "text", placeholder = "" }) {
  return (
    <div className="flex flex-col flex-1 min-w-[120px]">
      <label htmlFor={id} className="block text-gray-300 text-sm font-bold mb-1">
        {label}
      </label>
      <input id={id} type={type} value={value} onChange={onChange} placeholder={placeholder} className="rounded-md border border-slate-600 p-2 bg-slate-700 text-gray-200 placeholder-gray-400 w-full" />
    </div>
  );
}

function SelectInput({ label, value, onChange, options, id }) {
  return (
    <div className="flex flex-col flex-1 min-w-[120px]">
      <label htmlFor={id} className="block text-gray-300 text-sm font-bold mb-1">
        {label}
      </label>
      <select id={id} value={value} onChange={onChange} className="rounded-md border border-slate-600 p-2 bg-slate-700 text-gray-200 w-full">
        {options.map((opt) => (
          <option key={opt.value || opt} value={opt.value || opt}>
            {opt.label || opt}
          </option>
        ))}
      </select>
    </div>
  );
}
// --- End of Input components ---

export default function ChatSimulatorPage() {
  const [colorsData, setColorsData] = useState(defaultColors);
  const [scriptData, setScriptData] = useState(defaultScript);
  const [editorLog, setEditorLog] = useState(["App loaded – ready!"]);
  const [chatTitle, setChatTitle] = useState("Chat");
  const [profileImageSrc, setProfileImageSrc] = useState("/textAppImg/dog.jpg");

  const [openaiKey, setOpenaiKey] = useState(process.env.REACT_APP_OPENAI_API_KEY || "");
  const ttsCache = useRef(new Map());
  const [displayedMessages, setDisplayedMessages] = useState([]);
  const [currentTimeString, setCurrentTimeString] = useState("");
  const [showSpeakerIcon, setShowSpeakerIcon] = useState(true);
  const [playSendSFX, setPlaySendSFX] = useState(true);
  const [useSolidChatMessagesBg, setUseSolidChatMessagesBg] = useState(false); // New state for chat messages background
  const timers = useRef([]);
  const chatRef = useRef();
  const preloadedAudio = useRef([]);
  const BUFFER_MS = 250;
  const [videoUrl, setVideoUrl] = useState("/textAppImg/sampleBg.mp4");
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");
  const [openaiInstance, setOpenaiInstance] = useState(null);

  const AUTOMATEIT_HOSTING_URL = typeof window !== 'undefined' ? window.location.origin : (process.env.REACT_APP_PUBLIC_URL || 'https://your-automateit-app-name.web.app');

  const log = useCallback((msg) => setEditorLog((p) => [...p.slice(-Math.min(p.length, 19)), msg]), []);

  useEffect(() => {
    if (openaiKey) {
      try {
        setOpenaiInstance(new OpenAI({ apiKey: openaiKey, dangerouslyAllowBrowser: true }));
        log("OpenAI API key set and instance created.");
      } catch (error) {
        console.error("Error initializing OpenAI instance:", error);
        log("❌ Error initializing OpenAI. Check API key and network.");
        setOpenaiInstance(null);
      }
    } else {
      setOpenaiInstance(null);
      log("OpenAI API key not set. TTS features requiring OpenAI will be disabled.");
    }
  }, [openaiKey, log]);

  const showMessageInPreview = useCallback((msg) =>
    setDisplayedMessages((p) => [...p, { ...msg, sent: msg.sender?.toLowerCase() === "you", playing: false }]),
    []
  );

  const fetchOpenAITTS = useCallback(async (text, voice = "alloy", vibe = {}) => {
    const vibeObject = vibe || {};
    const vibeString = Object.entries(vibeObject).filter(([k, v]) => k !== 'open' && k !== 'preset' && v).map(([k, v]) => `${k}:${v}`).join(',');
    const key = `tts:${voice}:${text}:${vibeString}`;
    if (ttsCache.current.has(key)) return ttsCache.current.get(key);

    try {
      const storageRefInstance = ref(storage, `tts/${sanitize(key)}.mp3`);
      const url = await getDownloadURL(storageRefInstance);
      const blob = await (await fetch(url)).blob();
      const duration = await audioDuration(blob);
      const result = { url, duration };
      ttsCache.current.set(key, result);
      log(`🔊 TTS loaded from Firebase cache: ${text.substring(0, 20)}...`)
      return result;
    } catch (_) {
      log(`TTS for "${text.substring(0, 20)}..." not in Firebase cache. Attempting generation.`);
    }

    if (!openaiKey || !openaiInstance) {
      log("OpenAI API key not set or instance not ready. Skipping TTS generation.");
      return null;
    }

    log(`Generating TTS via OpenAI for: "${text.substring(0, 20)}..."`);
    try {
      const response = await openaiInstance.audio.speech.create({ model: "gpt-4o-mini-tts", input: text, voice, response_format: "mp3" });
      const blob = await response.blob();
      const duration = await audioDuration(blob);
      const filename = `tts/${sanitize(key)}.mp3`;
      const newStorageRefInstance = ref(storage, filename);
      await uploadBytes(newStorageRefInstance, blob, { contentType: "audio/mpeg" });
      const url = await getDownloadURL(newStorageRefInstance);
      const result = { url, duration };
      ttsCache.current.set(key, result);
      log(`✅ TTS generated and saved: ${text.substring(0, 20)}...`);
      return result;
    } catch (error) {
      console.error("Error in TTS fetch/upload:", error);
      log(`❌ Error during TTS processing for: "${text.substring(0, 20)}..." - ${error.message}`);
      return null;
    }
  }, [openaiKey, openaiInstance, log]);

  const fetchSFXData = useCallback(async (sfxSrc) => {
    const key = `sfx:${sfxSrc}`;
    if (ttsCache.current.has(key)) return ttsCache.current.get(key);
    const publicUrl = sfxSrc.startsWith('http') ? sfxSrc : `${AUTOMATEIT_HOSTING_URL}/sounds/${sfxSrc}`;
    try {
      log(`Attempting to fetch SFX: ${publicUrl}`);
      const response = await fetch(publicUrl);
      if (!response.ok) throw new Error(`SFX not found at ${publicUrl} (status: ${response.status})`);
      const blob = await response.blob();
      const duration = await audioDuration(blob);
      const result = { url: publicUrl, duration };
      ttsCache.current.set(key, result);
      log(`🔉 SFX loaded: ${sfxSrc}, Duration: ${duration.toFixed(2)}s`);
      return result;
    } catch (error) {
      console.error(`Error fetching SFX ${sfxSrc}:`, error);
      log(`❌ Error fetching SFX: ${sfxSrc} - ${error.message}. Ensure it's in /public/sounds and accessible via ${AUTOMATEIT_HOSTING_URL}.`);
      return { url: publicUrl, duration: 1, error: `Failed to load SFX: ${sfxSrc}` };
    }
  }, [log, AUTOMATEIT_HOSTING_URL]);

  const preloadAllAudio = useCallback(async () => {
    log("🔄 Starting Audio Preloading (TTS & SFX)...");
    const results = []; let allSucceeded = true;
    for (let i = 0; i < scriptData.length; i++) {
      const msg = scriptData[i]; let audioInfo = null;
      if (msg.type === "text" && msg.text) {
        if (!openaiKey) { log(`❌ OpenAI API Key not set. Cannot preload TTS for: "${msg.text.substring(0, 10)}..."`); allSucceeded = false; results[i] = null; continue; }
        log(`🔄 Preloading TTS for #${i + 1}: “${msg.text.substring(0, 20)}...”`);
        audioInfo = await fetchOpenAITTS(msg.text, msg.voice, msg.vibe);
      } else if (msg.type === "soundEffect" && msg.src) {
        log(`🔄 Preloading SFX for #${i + 1}: “${msg.src}”`);
        audioInfo = await fetchSFXData(msg.src);
      }
      results[i] = audioInfo;
      if (audioInfo?.duration) {
        const ms = Math.round(audioInfo.duration * 1000) + (msg.type === "text" ? BUFFER_MS : 0);
        if (msg.type === "text" && Math.abs(ms - msg.delay) > 300) {
          setScriptData((p) => p.map((m, j) => (j === i ? { ...m, delay: ms } : m)));
          log(`⏱️ Adjusted delay for TTS #${i + 1} → ${ms}ms`);
        }
      } else if (msg.type === "text" && msg.text && !audioInfo) {
        log(`❌ Couldn’t preload TTS for #${i + 1}`); allSucceeded = false;
      } else if (msg.type === "soundEffect" && msg.src && (!audioInfo || audioInfo.error)) { log(`❌ Couldn’t preload SFX for #${i + 1}: ${msg.src}`); }
    }
    preloadedAudio.current = results;
    log(allSucceeded ? "✅ Audio Preloading complete." : "⚠️ Audio Preloading completed with some errors.");
    return allSucceeded;
  }, [scriptData, fetchOpenAITTS, fetchSFXData, log, openaiKey, BUFFER_MS]);

  const playScript = useCallback(async () => {
    const audioReady = await preloadAllAudio();
    if (!audioReady && !window.confirm("Some audio could not be preloaded. Continue playback with missing audio/potential errors?")) { log("Playback cancelled by user due to audio errors."); return; }
    timers.current.forEach(clearTimeout); timers.current = []; setDisplayedMessages([]); log("▶️ Starting script playback…");
    for (let i = 0; i < scriptData.length; i++) {
      const m = scriptData[i]; const isSent = m.sender?.toLowerCase() === "you"; const audioClip = preloadedAudio.current[i];
      if (m.type === "soundEffect") {
        if (audioClip?.url && !audioClip.error) {
          log(`🔉 Playing sound effect: ${m.src} from ${audioClip.url}`); const audio = new Audio(audioClip.url); audio.play().catch(e => console.error("Error playing sound effect:", e));
          await sleep(audioClip.duration ? audioClip.duration * 1000 : (Number(m.delay) || 0));
        } else { log(`🔇 SFX ${m.src} not loaded or error, using delay.`); await sleep(Number(m.delay) || 0); }
        continue;
      }
      if (playSendSFX) {
        const whooshSfxKey = isSent ? "iphoneSwoosh.mp3" : "dingsfx.mp3"; const sfxData = await fetchSFXData(whooshSfxKey);
        if (sfxData?.url && !sfxData.error) { const notify = new Audio(sfxData.url); notify.play().catch(e => console.error("Error playing SFX:", e)); await sleep(150); }
        else { log(`SFX ${whooshSfxKey} not available for playback.`); await sleep(150); }
      }
      showMessageInPreview(m);
      setDisplayedMessages((prev) => prev.map((msg, j) => j === prev.length - 1 ? { ...msg, playing: true } : msg));
      setTimeout(() => { if (chatRef.current) { chatRef.current.scrollTop = chatRef.current.scrollHeight; } }, 50);
      if (audioClip?.url && m.type === "text" && !audioClip.error) {
        await new Promise((resolve) => { const audio = new Audio(audioClip.url); audio.onended = resolve; audio.onerror = () => { console.error("Error playing TTS audio:", audioClip.url); resolve(); }; audio.play().catch(e => { console.error("Error playing TTS audio:", e); resolve(); }); });
      } else { if (m.type === "text" && (!audioClip?.url || audioClip.error)) log(`🔇 No audio or error for "${m.text.substring(0, 20)}...", using delay.`); await sleep(Number(m.delay) || 0); }
      setDisplayedMessages((prev) => prev.map((msg, j) => j === prev.length - 1 ? { ...msg, playing: false } : msg));
    }
    log("✅ Playback complete.");
  }, [scriptData, preloadAllAudio, playSendSFX, log, showMessageInPreview, fetchSFXData]);

  const previewVoiceVibe = useCallback(async (msg) => {
    if (!openaiKey || !openaiInstance) { alert("Please enter your OpenAI API key."); return; }
    log(`🎙️ Generating voice preview for: "${msg.text.substring(0, 20)}..."`);
    try {
      const response = await openaiInstance.audio.speech.create({ model: "gpt-4o-mini-tts", voice: msg.voice || "alloy", input: msg.text });
      const audioBlob = await response.blob(); await playAudioFromBlob(audioBlob);
    } catch (err) { console.error("TTS preview failed:", err); alert(`Failed to generate voice preview: ${err.message}`); }
  }, [openaiKey, openaiInstance, log]);

  const onDragEnd = (result) => {
    if (!result.destination) return; const items = Array.from(scriptData); const [reorderedItem] = items.splice(result.source.index, 1); items.splice(result.destination.index, 0, reorderedItem); setScriptData(items); log("📦 Script reordered");
  };
  const addMessage = (index) => { const newMsg = { type: "text", text: "", sender: "You", delay: 1000, voice: "alloy", collapsed: true, vibe: { ...(vibePresets["No Vibe"] || {}), open: false, preset: "No Vibe" } }; setScriptData((prev) => { const copy = [...prev]; typeof index === "number" ? copy.splice(index + 1, 0, newMsg) : copy.push(newMsg); return copy; }); log("Added a new message"); };
  const addImageMessage = (index) => { const newMsg = { type: "image", src: "", sender: "You", delay: 2500, collapsed: true }; setScriptData((prev) => { const copy = [...prev]; typeof index === "number" ? copy.splice(index + 1, 0, newMsg) : copy.push(newMsg); return copy; }); log("🖼️ Added image message"); };
  const addSoundEffect = (index) => { const defaultSfx = (availableSoundEffects && availableSoundEffects.length > 0) ? availableSoundEffects[0].value : "Bruh.mp3"; const newMsg = { type: "soundEffect", src: defaultSfx, sender: "None", delay: 1000, collapsed: true }; setScriptData((prev) => { const copy = [...prev]; typeof index === "number" ? copy.splice(index + 1, 0, newMsg) : copy.push(newMsg); return copy; }); log("🔊 Added sound effect message"); };
  const deleteMessage = (index) => { setScriptData((prev) => prev.filter((_, i) => i !== index)); log(`Deleted message #${index + 1}`); };
  useEffect(() => { const tick = () => { const d = new Date(); const h = d.getHours(); const m = d.getMinutes(); setCurrentTimeString(`${h % 12 || 12}:${m < 10 ? "0" + m : m} ${h >= 12 ? "PM" : "AM"}`); }; tick(); const id = setInterval(tick, 1000); return () => clearInterval(id); }, []);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);
  useEffect(() => { setScriptData((prev) => prev.map((msg) => ({ ...msg, collapsed: msg.collapsed === undefined ? true : msg.collapsed }))); }, []);
  useEffect(() => { if (scriptData.length === 0) { setDisplayedMessages([]); } }, [scriptData]);

  const handleScriptUpload = (e) => {
    const file = e.target.files[0]; if (!file) return;
    const patchMessage = (msg) => { /* ... (unchanged) ... */ return msg; }; // Keep existing patch logic
    const reader = new FileReader();
    reader.onload = (ev) => { try { const loaded = JSON.parse(ev.target.result); const patched = loaded.map(patchMessage); setScriptData(patched); log("✅ Script loaded successfully."); } catch (err) { log(`❌ Error parsing script – ensure valid JSON. ${err.message}`); } };
    reader.readAsText(file);
  };

  const handleExportVideo = async () => {
    if (!auth.currentUser) {
      log("❌ User not authenticated.");
      setExportMessage("Error: Please sign in.");
      alert("You need to be signed in.");
      return;
    }
    if (!openaiKey) {
      log("❌ OpenAI API Key required.");
      setExportMessage("Error: OpenAI API Key required.");
      alert("Please enter OpenAI API Key.");
      return;
    }
    if (!functionsSecondary) {
      log("❌ Video rendering service not configured.");
      setExportMessage("Error: Video rendering service not configured.");
      alert("Video rendering service unavailable.");
      return;
    }

    setIsExporting(true);
    setExportMessage("🚀 Preparing data & preloading audio...");
    log("🚀 Preparing data for export & preloading audio...");

    try {
      const audioPreloadSuccess = await preloadAllAudio();
      if (!audioPreloadSuccess && !window.confirm("Audio preloading had issues. Some audio might be missing or incorrect. Continue?")) {
        log("Export cancelled by user due to audio preloading issues.");
        setExportMessage("Export cancelled due to audio errors.");
        setIsExporting(false);
        return;
      }
      log(audioPreloadSuccess ? "✅ Audio preloading successful." : "⚠️ Audio preloading completed with issues (continuing).");

      setExportMessage("🔗 Uploading assets (if needed)...");
      log("🔗 Uploading assets (if needed)...");

      const uploadIfNeeded = async (urlOrDataUrl, pathPrefix) => {
        // ...(same uploadIfNeeded function as before)...
        if (!urlOrDataUrl || typeof urlOrDataUrl !== 'string') return null;
        if (urlOrDataUrl.startsWith('http://') || urlOrDataUrl.startsWith('https://')) {
          log(`Asset ${pathPrefix} already a remote URL: ${urlOrDataUrl.substring(0, 50)}...`);
          return urlOrDataUrl;
        }
        if (urlOrDataUrl.startsWith('blob:') || urlOrDataUrl.startsWith('data:')) {
          try {
            const blob = await fetch(urlOrDataUrl).then(r => r.blob());
            let extension = blob.type.split('/')[1] || 'bin';
            if (extension === 'octet-stream' && urlOrDataUrl.startsWith('data:video')) extension = 'mp4';
            if (extension === 'octet-stream' && urlOrDataUrl.startsWith('data:image')) extension = 'png';
            const fileName = `${pathPrefix}-${Date.now()}.${extension}`;
            const storageRefInstance = ref(storage, `remotion_assets/${auth.currentUser.uid}/${fileName}`);
            log(`Uploading local asset for ${pathPrefix} as ${fileName}...`);
            await uploadBytes(storageRefInstance, blob);
            const downloadUrl = await getDownloadURL(storageRefInstance);
            log(`✅ Upload complete for ${pathPrefix}: ${downloadUrl.substring(0, 60)}...`);
            return downloadUrl;
          } catch (uploadError) {
            console.error(`Failed to upload asset for ${pathPrefix}:`, uploadError);
            log(`❌ Failed to upload asset for ${pathPrefix}: ${uploadError.message}`);
            throw new Error(`Asset upload failed for ${pathPrefix}: ${uploadError.message}`);
          }
        }
        log(`Asset for ${pathPrefix} is not a remote, blob, or data URL: ${urlOrDataUrl.substring(0, 50)}... Invalid.`);
        return null; // Explicitly return null for unhandled cases
      };

      const finalVideoUrl = videoUrl ? await uploadIfNeeded(videoUrl, "backgroundVideo") : null;
      const finalProfileImageSrc = profileImageSrc ? await uploadIfNeeded(profileImageSrc, "profileImage") : null;

      // Crucial Check: Ensure essential URLs are valid if they were supposed to be uploaded
      if (videoUrl && !finalVideoUrl) {
        throw new Error("Background video URL is invalid or upload failed. Please select a valid video file.");
      }
      if (profileImageSrc && !finalProfileImageSrc) {
        throw new Error("Profile image URL is invalid or upload failed. Please select a valid image file.");
      }

      log("🔄 Processing script data for asset uploads...");
      setExportMessage("🔄 Processing script assets...");
      const processedScriptData = await Promise.all(
        scriptData.map(async (msg, index) => {
          let processedMsg = { ...msg };
          const audioInfo = preloadedAudio.current[index];

          if (audioInfo && audioInfo.url && !audioInfo.error) {
            processedMsg.audioUrl = audioInfo.url;
            processedMsg.audioDuration = audioInfo.duration;
          } else {
            processedMsg.audioUrl = null;
            processedMsg.audioDuration = null;
            if (msg.type === "text" && msg.text) log(`⚠️ No audio for msg #${index + 1} ('${msg.text.substring(0, 10)}...')`);
            else if (msg.type === "soundEffect" && msg.src) log(`⚠️ SFX '${msg.src}' for msg #${index + 1} not preloaded.`);
          }

          if (msg.type === 'image' && msg.src) {
            processedMsg.src = msg.src ? await uploadIfNeeded(msg.src, `chatImage-${index}`) : null;
            if (msg.src && !processedMsg.src) {
              log(`❌ Image for msg #${index + 1} ('${msg.text || msg.src.substring(0, 10)}') could not be prepared for upload.`);
              // Optionally, throw an error here if images in script are critical
              // throw new Error(`Failed to process image in script at index ${index}`);
            }
          }
          // Ensure all required fields for Zod are present in each script item
          // Zod schema expects: type, sender, delay. text/src/voice are optional. audioUrl/Duration are optional.
          if (!processedMsg.type || !processedMsg.sender || typeof processedMsg.delay !== 'number') {
            log(`ERROR: Script item at index ${index} is missing required fields (type, sender, or delay). Item:`, processedMsg);
            throw new Error(`Script item at index ${index} is malformed.`);
          }
          return processedMsg;
        })
      );
      log("✅ Script data assets processed.");
      setExportMessage("📦 Packaging data for Remotion...");

      // Ensure core data is not undefined before constructing props
      if (typeof chatTitle !== 'string' || chatTitle.trim() === '') {
        log("❌ Error: Chat title is empty or not set.");
        throw new Error("Chat title is required. Please enter a chat title.");
      }
      if (!colorsData || typeof colorsData !== 'object' || Object.keys(colorsData).length === 0) {
        log("❌ Error: Colors data is missing or invalid.");
        throw new Error("Color configuration is missing. Please reset colors or check setup.");
      }
      if (!processedScriptData) { // Should be an array, even if empty
        log("❌ Error: Processed script data is undefined.");
        throw new Error("Failed to process script data. It appears to be undefined.");
      }


      const propsForRemotion = {
        scriptData: processedScriptData, // Should be an array (can be empty)
        colors: colorsData,             // Should be an object
        videoUrl: finalVideoUrl,        // String (URL) or null
        chatTitle: chatTitle,           // Non-empty string
        profileImageSrc: finalProfileImageSrc, // String (URL) or null
        globalSettings: { showSpeakerIcon: showSpeakerIcon }, // Object
        fps: 30,                        // Number
        width: 720,                     // Number
        height: 1280,                   // Number
        userId: auth.currentUser.uid,   // String
      };

      log("📞 Calling Firebase Function 'initiateVideoRender' with props:");
      console.log("Props being sent to Remotion function:", JSON.parse(JSON.stringify(propsForRemotion))); // Deep log a clone
      log("Props being sent to Remotion function:", JSON.parse(JSON.stringify(propsForRemotion)));
      setExportMessage("⚙️ Sending render request to server...");

      const initiateVideoRender = httpsCallable(functionsSecondary, 'initiateVideoRender');
      const result = await initiateVideoRender(propsForRemotion);

      if (result.data.success) {
        log(`✅ Render job submitted: ${result.data.jobId || 'OK'}. Output: ${result.data.outputLocation}`);
        setExportMessage(`✅ Export started! Job ID: ${result.data.jobId || 'N/A'}. Output: ${result.data.outputLocation}`);
      } else {
        const errorDetails = result.data.details ? JSON.stringify(result.data.details) : "No additional details.";
        log(`❌ Error submitting render job: ${result.data.error || errorDetails}`);
        setExportMessage(`❌ Error: ${result.data.error || 'Failed to start. See console/logs.'}`);
        alert(`Server Error: ${result.data.error || 'Unknown error.'}\nDetails: ${errorDetails}`);
      }
    } catch (error) {
      console.error("Export error in handleExportVideo:", error);
      log(`❌ Export failed: ${error.message}`);
      setExportMessage(`❌ Export failed: ${error.message}`);
      alert(`Client-side Export Error: ${error.message}`);
    } finally {
      setIsExporting(false);
    }
  };

  // Determine the background color for the messages area based on the toggle
  const messagesAreaBackgroundColor = useSolidChatMessagesBg ? '#FFFFFF' : colorsData.chatBg;


  return (
    <div className="chat-generator container mx-auto p-2 sm:p-4 text-gray-100">
      <style>{`
         /* ... (styles from previous version, ensure they are complete) ... */
         .chat-generator .main-grid { display: grid; grid-template-columns: 1fr; gap: 1.5rem; }
        @media (min-width: 1024px) { .chat-generator .main-grid { grid-template-columns: minmax(300px, 1fr) minmax(320px, 405px) minmax(300px, 1fr); } }
        .chat-generator .script-item { transition: border-color 0.2s ease-in-out; }
        .chat-generator .editor-log { margin-top: 1rem; padding: 0.5rem; background-color: #1f2937; border: 1px solid #374151; border-radius: 0.375rem; max-height: 150px; overflow-y: auto; font-size: 0.8rem; line-height: 1.2; color: #d1d5db; }
        .chat-generator .editor-log p { margin-bottom: 0.25rem; font-family: monospace; }
        .chat-generator .chat-bubble { display: flex; flex-direction: column; }
        .chat-generator .chat-bubble.sent { margin-left: auto; align-items: flex-end; }
        .chat-generator .chat-bubble.received { margin-right: auto; align-items: flex-start; }
        .chat-generator .export-message { margin-top: 0.5rem; padding: 0.75rem 1rem; border-radius: 0.375rem; font-size: 0.9rem; text-align: center; }
        .chat-generator .export-message.success { background-color: #059669; color: #d1fae5; border: 1px solid #047857;}
        .chat-generator .export-message.error { background-color: #b91c1c; color: #fee2e2; border: 1px solid #991b1b;}
        .chat-generator .export-message.info { background-color: #0369a1; color: #e0f2fe; border: 1px solid #075985;}
        .chat-generator label { color: #d1d5db; }
        .chat-generator input[type="text"], .chat-generator input[type="password"], .chat-generator input[type="number"], .chat-generator input[type="url"], .chat-generator select, .chat-generator textarea {
             background-color: #374151; color: #e5e7eb; border-color: #4b5563; border-radius: 0.375rem; padding: 0.5rem; width: 100%; /* Ensure inputs take full width of their cell */
        }
        .chat-generator input[type="color"] { border-color: #4b5563; width: 100%; }
        .chat-generator ::placeholder { color: #9ca3af; }
        .chat-generator .file-input-styles { display: block; width: 100%; font-size: 0.875rem; color: #9ca3af; }
        .chat-generator .file-input-styles::-webkit-file-upload-button { margin-right: 1rem; padding: 0.5rem 1rem; border-radius: 9999px; border-width: 0px; font-size: 0.875rem; font-weight: 600; cursor: pointer; color: white; }
        .chat-generator button { transition: background-color 0.2s ease-in-out, transform 0.1s ease; }
        .chat-generator button:active { transform: scale(0.98); }
        .chat-preview-container { /* Styles for the main phone-like preview box */
            border-radius: 1.5rem; /* More rounded corners for the phone shape */
            box-shadow: 0 10px 25px -5px rgba(0,0,0,0.2), 0 20px 40px -10px rgba(0,0,0,0.2); /* Softer, deeper shadow */
            width: 100%; 
            /* height: 100%; REMOVED to allow aspect ratio to work */
            position: relative; 
            overflow: hidden; 
            background-color: #000000; /* Fallback BG for video container */
        }
        .chat-ui-block { /* The actual chat interface (header + messages) */
            width: 90%; /* As per original, can be adjusted */
            max-width: calc(100% - 2rem); /* As per original */
            border-radius: 0.75rem; /* Rounded corners for the chat block itself */
            overflow: hidden; /* To clip children like header/messages to rounded corners */
            box-shadow: 0 4px 12px rgba(0,0,0,0.15); /* Inner shadow for the chat block */
        }
      `}</style>
      <h1 className="mb-6 text-center text-3xl font-semibold text-sky-300">
        Chat Simulator Video Generator
      </h1>
      <div className="main-grid">
        {/* Column 1: Global Settings & Controls */}
        <div className="order-1 lg:order-1 space-y-6">
          <div className="rounded-lg bg-slate-800/70 p-4 sm:p-6 shadow-xl">
            <h2 className="mb-4 text-xl font-semibold text-sky-400">Global Settings</h2>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3 mb-4">
              <ColorInput id="sent" label="Sent Bubble" value={colorsData.sent} onChange={(e) => setColorsData({ ...colorsData, sent: e.target.value })} />
              <ColorInput id="received" label="Received Bubble" value={colorsData.received} onChange={(e) => setColorsData({ ...colorsData, received: e.target.value })} />
              <ColorInput id="text" label="Text (Bubbles)" value={colorsData.text} onChange={(e) => setColorsData({ ...colorsData, text: e.target.value })} />
              <ColorInput id="header" label="Header BG" value={colorsData.headerBg} onChange={(e) => setColorsData({ ...colorsData, headerBg: e.target.value })} />
              <ColorInput id="chatbg" label="Messages Area BG" value={colorsData.chatBg} onChange={(e) => setColorsData({ ...colorsData, chatBg: e.target.value })} />
              <ColorInput id="svgIcon" label="Header Icons" value={colorsData.svgIcon} onChange={(e) => setColorsData({ ...colorsData, svgIcon: e.target.value })} />
              <ColorInput id="titleText" label="Chat Title Text" value={colorsData.titleText} onChange={(e) => setColorsData({ ...colorsData, titleText: e.target.value })} />
            </div>

            <div className="flex items-center gap-2 mb-4"> {/* Moved checkbox up */}
              <input id="chatbg-transparent-toggle" type="checkbox" checked={!useSolidChatMessagesBg} onChange={() => setUseSolidChatMessagesBg(p => !p)} className="h-4 w-4 text-sky-500 border-slate-500 rounded focus:ring-sky-400 bg-slate-600" />
              <label htmlFor="chatbg-transparent-toggle" className="text-sm text-gray-300">Use Transparent Messages BG (uses color above with its alpha)</label>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-300 mb-1" htmlFor="profile-upload">Profile Image</label>
              <input id="profile-upload" type="file" accept="image/*"
                onChange={(e) => { const f = e.target.files[0]; if (!f) return; const r = new FileReader(); r.onload = (ev) => { setProfileImageSrc(ev.target.result); log("🖼️ Profile image updated."); }; r.readAsDataURL(f); }}
                className="file-input-styles file:bg-sky-700/50 file:text-sky-300 hover:file:bg-sky-600/50" />
              {profileImageSrc && <img src={profileImageSrc} alt="profile preview" className="mt-2 h-12 w-12 rounded-full object-cover border-2 border-slate-600" onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/48x48/374151/9ca3af?text=PFP'; }} />}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-bold text-gray-300 mb-1">Background Video</label>
              <input type="file" accept="video/*"
                onChange={(e) => { const file = e.target.files[0]; if (file) { const objectUrl = URL.createObjectURL(file); setVideoUrl(objectUrl); log("🎬 Background video updated."); } }}
                className="file-input-styles file:bg-green-700/50 file:text-green-300 hover:file:bg-green-600/50" />
            </div>

            <div className="flex items-center gap-2 mb-2">
              <input id="icon-toggle" type="checkbox" checked={showSpeakerIcon} onChange={() => setShowSpeakerIcon((p) => !p)} className="h-4 w-4 text-sky-500 border-slate-500 rounded focus:ring-sky-400 bg-slate-600" />
              <label htmlFor="icon-toggle" className="text-sm text-gray-300">Show 🔊 Icon in Preview</label>
            </div>
            <div className="flex items-center gap-2 mb-4">
              <input id="fx-toggle" type="checkbox" checked={playSendSFX} onChange={() => setPlaySendSFX((p) => !p)} className="h-4 w-4 text-sky-500 border-slate-500 rounded focus:ring-sky-400 bg-slate-600" />
              <label htmlFor="fx-toggle" className="text-sm text-gray-300">Play Message SFX in Preview</label>
            </div>

            <div className="flex flex-col mb-4">
              <label htmlFor="openai-key" className="block text-sm font-bold text-gray-300 mb-1">OpenAI API Key</label>
              <input id="openai-key" type="password" placeholder="sk-..." value={openaiKey} onChange={(e) => setOpenaiKey(e.target.value)} />
            </div>

            <div className="flex flex-col gap-3">
              <TextInput id="title" label="Chat Title" value={chatTitle} onChange={(e) => setChatTitle(e.target.value)} />
              <div className="flex gap-2 flex-wrap">
                <button onClick={() => { setColorsData(defaultColors); setUseSolidChatMessagesBg(false); log("🎨 Colors reset."); }} className="flex-1 rounded-md bg-slate-600 px-4 py-2 font-semibold text-gray-200 hover:bg-slate-500">Reset Colors</button>
                <button onClick={playScript} className="flex-1 rounded-md bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-700">Play Preview</button>
              </div>
              <button onClick={handleExportVideo} disabled={isExporting || !auth.currentUser || !openaiKey || !functionsSecondary} title={!auth.currentUser ? "Sign in to export" : !openaiKey ? "OpenAI Key required" : !functionsSecondary ? "Service unavailable" : "Export video"}
                className="w-full rounded-md bg-sky-600 px-4 py-2.5 font-semibold text-white hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed" >
                {isExporting ? "Exporting..." : "🚀 Export to MP4 (Server)"}
              </button>
              {exportMessage && <div className={`export-message ${exportMessage.includes("Error") || exportMessage.includes("failed") ? 'error' : exportMessage.includes("started") ? 'success' : 'info'}`}>{exportMessage}</div>}
            </div>
          </div>
          <div className="rounded-lg bg-slate-800/70 p-4 sm:p-6 shadow-xl"><h2 className="mb-4 text-xl font-semibold text-sky-400">Upload Script (.json)</h2><input type="file" accept=".json" onChange={handleScriptUpload} className="file-input-styles file:bg-indigo-700/50 file:text-indigo-300 hover:file:bg-indigo-600/50" /></div>
          <div className="editor-log" id="editor-log">{editorLog.map((l, i) => <p key={i}>{l}</p>)}</div>
        </div>

        {/* Column 2: Chat Preview */}
        <div className="order-2 lg:order-2 flex flex-col items-center justify-start min-h-[600px] lg:min-h-screen bg-slate-900/50 p-4 rounded-lg shadow-inner">
          {/* Ensure this outer div allows the aspect-ratio div to center and not be overly constrained by min-height if the width is small */}
          <div className="w-full flex justify-center">
            <div className="chat-preview-container aspect-[9/16] w-full max-w-[405px]"> {/* Applied max-width here too for consistency */}
              {videoUrl ? (
                <video key={videoUrl} src={videoUrl} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover z-0"
                  onError={(e) => { e.target.style.display = 'none'; log(`❌ Error loading preview BG video.`); }} />
              ) : (<div className="absolute inset-0 w-full h-full object-cover z-0 bg-slate-700 flex items-center justify-center text-slate-500">No BG Video</div>)}

              <div className="absolute inset-0 w-full h-full flex flex-col items-center justify-start z-10 pt-16 sm:pt-20" style={{ backgroundColor: 'transparent' }}>
                <div className="chat-ui-block flex flex-col">
                  <div className="p-2 z-20 w-full rounded-t-xl flex justify-between items-center" style={{ backgroundColor: colorsData.headerBg }}>
                    <img src={`${AUTOMATEIT_HOSTING_URL}/textAppImg/back_button.svg`} alt="back button icon" className="h-6 w-6 opacity-75" style={{ fill: colorsData.svgIcon }} onError={(e) => { e.target.style.display = 'none'; }} />
                    <div className='flex items-center gap-2'>
                      {profileImageSrc && <img src={profileImageSrc} alt="profile" className="h-8 w-8 rounded-full object-cover border border-slate-500" onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/32x32/CCC/333?text=PFP'; }} />}
                      <div className="text-center text-sm font-semibold" style={{ color: colorsData.titleText }}>{chatTitle}</div>
                    </div>
                    <img src={`${AUTOMATEIT_HOSTING_URL}/textAppImg/video_camera.svg`} alt="video camera icon" className="h-6 w-6 opacity-75" style={{ fill: colorsData.svgIcon }} onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                  <div
                    className="px-3 py-2 overflow-y-auto rounded-b-xl w-full flex-grow"
                    style={{
                      paddingTop: '16px',
                      backgroundColor: messagesAreaBackgroundColor,
                    }}
                    ref={chatRef}
                  >
                    {displayedMessages.map((m, i) =>
                      m.type === 'soundEffect' ? null : (
                        <div key={i} className={`chat-bubble flex mb-2 ${m.sent ? 'sent justify-end' : 'received justify-start'}`}>
                          <div className="p-2 rounded-lg shadow max-w-[70%]" style={{ background: m.sent ? colorsData.sent : colorsData.received, color: colorsData.text, borderRadius: m.sent ? '18px 5px 18px 18px' : '5px 18px 18px 18px' }}>
                            {m.type === 'image' ? (<img src={m.src} alt="chat content" className="max-w-full h-auto" style={{ maxHeight: '200px', objectFit: 'contain', borderRadius: '8px' }} onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/150x150/EEE/777?text=ImgFail'; }} />
                            ) : (<span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</span>)}
                            {m.playing && showSpeakerIcon && m.type === 'text' && (<span style={{ marginLeft: '8px', fontSize: '0.8em' }}>🔊</span>)}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="text-center text-xs text-slate-400 mt-2">{currentTimeString}</div>
        </div>

        {/* Column 3: Script Editor (Structure largely unchanged, ensure all inputs are full width within their cells) */}
        <div className="order-3 lg:order-3 mt-10 lg:mt-0 rounded-lg bg-slate-800/70 p-4 sm:p-6 shadow-xl">
          <h2 className="mb-4 text-xl font-semibold text-sky-400">Edit Script</h2>
          <div className="relative z-0 max-h-[70vh] overflow-y-auto pr-2">
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="script">
                {(provided) => (
                  <div ref={provided.innerRef} {...provided.droppableProps}>
                    {scriptData.map((msg, idx) => (
                      <Draggable key={idx.toString()} draggableId={`item-${idx}`} index={idx}>
                        {(providedDrag) => (
                          <div ref={providedDrag.innerRef} {...providedDrag.draggableProps} className="script-item mb-3 rounded-md shadow-md border-l-4 bg-slate-700" style={{ borderColor: msg.type === "soundEffect" ? "#facc15" : msg.type === "image" ? "#22c55e" : msg.collapsed ? "#4b5563" : "#38bdf8", ...providedDrag.draggableProps.style }}>
                            <div className="flex items-center justify-between bg-slate-600 hover:bg-slate-500 p-3 w-full" {...providedDrag.dragHandleProps}>
                              <button onClick={() => setScriptData((prev) => prev.map((m, i) => i === idx ? { ...m, collapsed: !m.collapsed } : m))} className="text-left font-medium text-sm flex-grow text-gray-200" >
                                #{idx + 1} - {msg.type || "text"} - {msg.text?.slice(0, 20) || msg.src?.split('/').pop()?.slice(0, 20) || 'Effect'}{msg.text?.length > 20 || msg.src?.split('/').pop()?.length > 20 ? '...' : ''}
                              </button>
                              <div className="flex gap-1 ml-2 flex-shrink-0">
                                <button onClick={() => addMessage(idx)} className="rounded bg-sky-700 px-2 py-1 text-xs text-sky-200 hover:bg-sky-600">Msg</button>
                                <button onClick={() => addImageMessage(idx)} className="rounded bg-green-700 px-2 py-1 text-xs text-green-200 hover:bg-green-600">Img</button>
                                <button onClick={() => addSoundEffect(idx)} className="rounded bg-yellow-600 px-2 py-1 text-xs text-yellow-100 hover:bg-yellow-500">SFX</button>
                                <button onClick={() => deleteMessage(idx)} className="rounded bg-red-700 px-2 py-1 text-xs text-red-200 hover:bg-red-600">✕</button>
                              </div>
                            </div>
                            {!msg.collapsed && (
                              <div className="p-3 border-t border-slate-600 bg-slate-700 space-y-3">
                                {msg.type === "image" && ( /* ... Image inputs ... */ <div className="flex flex-wrap gap-3 items-end"><SelectInput id={`sender-img-${idx}`} label="Sender" value={msg.sender} options={["You", "Friend"]} onChange={(e) => setScriptData(prev => prev.map((m, i) => i === idx ? { ...m, sender: e.target.value } : m))} /><TextInput id={`delay-img-${idx}`} label="Delay (ms)" type="number" value={msg.delay} onChange={(e) => setScriptData(prev => prev.map((m, i) => i === idx ? { ...m, delay: Number(e.target.value) } : m))} /><div className="flex flex-col flex-grow"><label className="text-xs font-bold text-gray-300 mb-1">Upload Image</label><input type="file" accept="image/*" className="file-input-styles text-sm w-full file:bg-purple-700/50 file:text-purple-300 hover:file:bg-purple-600/50" onChange={(e) => { const file = e.target.files[0]; if (!file) return; const reader = new FileReader(); reader.onload = (ev) => setScriptData(prev => prev.map((m, i) => i === idx ? { ...m, src: ev.target.result } : m)); reader.readAsDataURL(file); }} /></div>{msg.src && <img src={msg.src} alt="upload preview" className="h-12 w-12 rounded object-cover border border-slate-500 ml-2" onError={(e) => { e.target.style.display = 'none'; }} />}</div>)}
                                {msg.type === "soundEffect" && ( /* ... SFX inputs ... */ <div className="flex flex-wrap gap-3 items-end"><SelectInput id={`sender-sfx-${idx}`} label="Sender (Visual)" value={msg.sender} options={["You", "Friend", "None"]} onChange={(e) => setScriptData(prev => prev.map((m, i) => i === idx ? { ...m, sender: e.target.value } : m))} /><TextInput id={`delay-sfx-${idx}`} label="Delay (ms)" type="number" value={msg.delay} onChange={(e) => setScriptData(prev => prev.map((m, i) => i === idx ? { ...m, delay: Number(e.target.value) } : m))} /><div className="flex-grow"><SelectInput id={`sound-${idx}`} label="Sound Effect" value={msg.src} options={availableSoundEffects} onChange={(e) => setScriptData(prev => prev.map((m, i) => i === idx ? { ...m, src: e.target.value } : m))} /></div><button onClick={async () => { const sfxAudio = await fetchSFXData(msg.src); if (sfxAudio?.url && !sfxAudio.error) new Audio(sfxAudio.url).play().catch(err => log(`Error playing SFX preview: ${err.message}`)); else log(`Could not play SFX preview for ${msg.src}`); }} className="rounded bg-sky-600 px-2 py-1 text-xs text-white hover:bg-sky-700 self-end h-10">▶</button></div>)}
                                {msg.type === "text" && ( /* ... Text inputs ... */ <> <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"> <div className="col-span-full"> <label htmlFor={`text-area-${idx}`} className="block text-gray-300 text-sm font-bold mb-1">Text</label> <textarea id={`text-area-${idx}`} value={msg.text} onChange={(e) => setScriptData(prev => prev.map((m, i) => i === idx ? { ...m, text: e.target.value } : m))} className="w-full min-h-[60px]" rows="3"></textarea> </div> <SelectInput id={`sender-txt-${idx}`} label="Sender" value={msg.sender} options={["You", "Friend"]} onChange={(e) => setScriptData(prev => prev.map((m, i) => i === idx ? { ...m, sender: e.target.value } : m))} /> <SelectInput id={`voice-${idx}`} label="Voice" value={msg.voice} options={voices} onChange={(e) => setScriptData(prev => prev.map((m, i) => i === idx ? { ...m, voice: e.target.value } : m))} /> <TextInput id={`delay-txt-${idx}`} label="Delay (ms)" type="number" value={msg.delay} onChange={(e) => setScriptData(prev => prev.map((m, i) => i === idx ? { ...m, delay: Number(e.target.value) } : m))} /> </div> <div className="mt-3 border-t border-slate-600 pt-3"> <div className="flex justify-between items-center gap-2 mb-2"> <label className="text-sm font-bold text-gray-300">Voice Vibe</label> <div className="flex gap-2 items-center"> <select className="text-xs border border-slate-500 rounded p-1 bg-slate-600 text-gray-200" value={msg.vibe?.preset || "No Vibe"} onChange={(e) => { const presetName = e.target.value; const baseForPreset = vibePresets[presetName] || vibePresets["No Vibe"] || {}; setScriptData(prev => prev.map((m, i) => i === idx ? { ...m, vibe: { ...(vibePresets["No Vibe"] || {}), ...baseForPreset, open: m.vibe?.open, preset: presetName } } : m)); }}>{Object.keys(vibePresets).map(name => <option key={name} value={name}>{name}</option>)}</select> <button onClick={() => setScriptData(prev => prev.map((m, i) => i === idx ? { ...m, vibe: { ...(msg.vibe || {}), open: !msg.vibe?.open } } : m))} className="text-xs text-sky-400 hover:underline">{msg.vibe?.open ? "Hide" : "Show"} Details</button> </div> </div> {msg.vibe?.open && (<div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs"> {(vibePresets["Custom"] ? Object.keys(vibePresets["Custom"]) : []).filter(k => k !== 'open' && k !== 'preset').map((key) => (<div key={key} className="flex flex-col"> <label htmlFor={`${key}-${idx}`} className="block text-gray-400 font-medium mb-1 capitalize">{key.replace(/([A-Z])/g, ' $1')}</label> <TextInput id={`${key}-${idx}`} type="text" placeholder={(vibePresets["Custom"] && vibePresets["Custom"][key]) || ""} value={msg.vibe?.[key] || ""} onChange={(e) => setScriptData(prev => prev.map((m, i) => i === idx ? { ...m, vibe: { ...(msg.vibe || {}), [key]: e.target.value } } : m))} /> </div>))} </div>)} <div className="mt-3"> <button onClick={() => previewVoiceVibe(msg)} className="rounded bg-purple-600 px-3 py-1 text-xs text-white hover:bg-purple-700">🔊 Preview Voice Vibe</button> </div> </div> </>)}
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </div>
          <button onClick={() => addMessage()} className="mt-4 w-full rounded-md bg-sky-700 px-4 py-2 font-semibold text-sky-200 hover:bg-sky-600">
            + Add New Message
          </button>
        </div>
      </div>
    </div>
  );
}
